import { useQuery } from "@tanstack/react-query";
import { SearchX } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { CheckboxField, SelectField, TextField } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { LoadMore, PageSizeSelect } from "@/components/ui/Paging";
import { formatDateTime, formatNumber } from "@/lib/format";
import { stepFocus, useShortcuts } from "@/lib/keyboard";
import { SEARCH_SIZES, ticketsApi } from "@/lib/tickets-api";
import { readPerPage, rememberPerPage, usePaged, type PerPage } from "@/lib/use-paged";

import { Highlight, StateBadge } from "./TicketBits";

function SearchForm({
  params,
  onSearch,
}: {
  params: URLSearchParams;
  onSearch: (next: URLSearchParams) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(params.get("q") ?? "");
  const [subjectOnly, setSubjectOnly] = useState(params.get("subject") === "1");
  const [from, setFrom] = useState(params.get("from") ?? "");
  const [to, setTo] = useState(params.get("to") ?? "");
  const [owner, setOwner] = useState(params.get("owner") ?? "");
  const owners = useQuery({ queryKey: ["tickets", "owners"], queryFn: ticketsApi.owners, staleTime: 60_000 });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (text.trim()) next.set("q", text.trim());
    if (subjectOnly) next.set("subject", "1");
    if (from) next.set("from", from);
    if (to) next.set("to", to);
    if (owner) next.set("owner", owner);
    onSearch(next);
  };

  return (
    <form onSubmit={submit} role="search" className="space-y-4 border-b border-line p-5">
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
        <TextField
          label={t("search.label")}
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          hint={t("search.hint")}
          autoFocus={!params.get("q")}
        />
        <div className="sm:pt-6">
          <Button type="submit" className="w-full sm:w-auto">
            {t("search.submit")}
          </Button>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CheckboxField
          label={t("search.subjectOnly")}
          checked={subjectOnly}
          onChange={(e) => setSubjectOnly(e.target.checked)}
          className="sm:pt-7"
        />
        <TextField
          label={t("tickets.filters.from")}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          label={t("tickets.filters.to")}
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <SelectField
          label={t("tickets.filters.owner")}
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
        >
          <option value="">{t("tickets.filters.allOwners")}</option>
          {owners.data?.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </SelectField>
      </div>
    </form>
  );
}

const PER_PAGE_KEY = "tv.search.perPage";
const ALL_CHUNK = 100;

export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const per = readPerPage(params.get("per"), SEARCH_SIZES, PER_PAGE_KEY, 25);
  const page = Math.max(0, Number(params.get("page") ?? "1") - 1) || 0;
  const options = {
    subjectOnly: params.get("subject") === "1",
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
    owner:
      params.get("owner") && /^\d+$/.test(params.get("owner") ?? "")
        ? Number(params.get("owner"))
        : undefined,
  };
  const searchKey = ["q", "subject", "from", "to", "owner"].map((k) => params.get(k) ?? "").join("|");
  const result = usePaged(
    ["search", searchKey],
    (offset, limit) => ticketsApi.search(q, offset, limit, options),
    per,
    page,
    ALL_CHUNK,
    q.trim().length > 0,
  );
  const data = q ? result.first : undefined;
  const items = q ? result.items : undefined;
  const { total, offset, size } = result;
  const here = `/search?${params.toString()}`;
  const numbers = items?.map((item) => item.number);
  const links = useRef<(HTMLAnchorElement | null)[]>([]);
  useShortcuts({ j: () => stepFocus(links.current, 1), k: () => stepFocus(links.current, -1) });

  // A ticket number typed in the search box opens that ticket directly.
  const exact = data?.exact;
  useEffect(() => {
    if (exact && page === 0) void navigate(`/tickets/${encodeURIComponent(exact)}`, { replace: true });
  }, [exact, page, navigate]);

  const goTo = (target: number, perPage: PerPage = per) => {
    const next = new URLSearchParams(params);
    next.delete("page");
    next.delete("per");
    if (perPage !== 25) next.set("per", String(perPage));
    if (perPage !== "all" && target > 0) next.set("page", String(target + 1));
    setParams(next);
  };
  const choosePer = (value: PerPage) => {
    rememberPerPage(PER_PAGE_KEY, value);
    goTo(0, value);
  };

  return (
    <>
      <PageHeader title={t("nav.search")} />
      <Card className="overflow-hidden">
        <SearchForm key={params.toString()} params={params} onSearch={setParams} />
        {!q ? (
          <p className="px-5 py-6 text-sm text-ink-muted">{t("search.intro")}</p>
        ) : !data || !items ? (
          <p className="px-5 py-6 text-sm text-ink-muted">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <EmptyState icon={SearchX} title={t("search.noResults", { q })}>
            {t("search.noResultsBody")}
          </EmptyState>
        ) : (
          <ol className="divide-y divide-line" aria-label={t("search.results")}>
            {items.map((item, index) => (
              <li key={item.id} className="space-y-1.5 px-5 py-4">
                <Link
                  ref={(element) => {
                    links.current[index] = element;
                  }}
                  to={`/tickets/${encodeURIComponent(item.number)}`}
                  state={{ from: here, numbers }}
                  className="font-medium break-words text-ink underline-offset-4 hover:text-primary hover:underline"
                >
                  <span className="text-ink-muted tabular-nums">#{item.number}</span>{" "}
                  <Highlight text={item.title || t("tickets.untitled")} terms={data.terms} />
                </Link>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-subtle">
                  <StateBadge name={item.state} category={item.state_category} />
                  <span>{item.queue}</span>
                  <span className="tabular-nums">{formatDateTime(item.created_at)}</span>
                  {item.owner && <span>{t("search.owner", { name: item.owner })}</span>}
                  {item.customer_user && <span className="break-all">{item.customer_user}</span>}
                  {item.matches > 1 && <span>{t("search.matches", { count: item.matches })}</span>}
                </div>
                {item.subject && item.subject !== item.title && (
                  <p className="text-sm break-words text-ink-muted">
                    <Highlight text={item.subject} terms={data.terms} />
                  </p>
                )}
                {item.snippet && (
                  <p className="text-sm break-words text-ink-muted">
                    <Highlight text={item.snippet} terms={data.terms} />
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
        {q && total > 0 && (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-sm">
            {per === "all" ? (
              <LoadMore
                loaded={items?.length ?? 0}
                total={total}
                hasMore={result.hasMore}
                loading={result.loadingMore}
                onLoad={result.loadMore}
              />
            ) : (
              <span className="text-ink-muted tabular-nums" aria-live="polite">
                {t("search.total", { count: total, formatted: formatNumber(total) })}
              </span>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <PageSizeSelect value={per} sizes={SEARCH_SIZES} onChange={choosePer} />
              {per !== "all" && (
                <>
                  <Button variant="secondary" disabled={page === 0} onClick={() => goTo(page - 1)}>
                    {t("common.previous")}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={offset + size >= total}
                    onClick={() => goTo(page + 1)}
                  >
                    {t("common.next")}
                  </Button>
                </>
              )}
            </div>
          </footer>
        )}
      </Card>
    </>
  );
}
